import { useCallback } from 'react';
import { useFile, useUI } from '../context/AppContext.jsx';
import { URPExporter } from '../exporter/URPExporter.js';
import { InstallationExporter } from '../exporter/InstallationExporter.js';
import { pluginRegistry } from '../plugins/PluginRegistry.js';

export function useExport() {
  const {
    parser,
    installationParser,
    currentFileData,
    currentInstallationData,
    currentFileName,
    currentInstallationFileName,
    currentWaypoints,
    currentFeatures,
  } = useFile();
  const { showStatus } = useUI();

  /**
   * Run all validators and return results
   * @returns {{valid: boolean, results: Array}} Validation results
   */
  const validateBeforeExport = useCallback(() => {
    const results = pluginRegistry.runAllValidators({
      waypoints: currentWaypoints,
      features: currentFeatures,
    });

    const allValid = results.every(r => r.valid);
    return { valid: allValid, results };
  }, [currentWaypoints, currentFeatures]);

  const exportURP = useCallback(async (skipValidation = false) => {
    if (!currentFileData) {
      showStatus('No file loaded to export', 'error');
      return;
    }

    try {
      // Run validation unless skipped
      if (!skipValidation) {
        const { valid, results } = validateBeforeExport();
        
        if (!valid) {
          const errorMessages = results
            .filter(r => !r.valid)
            .flatMap(r => r.messages);
          
          // Show warning but still allow export
          const warningCount = errorMessages.length;
          showStatus(`Warning: ${warningCount} validation issue(s) found. Exporting anyway...`, 'warning');
          
          // Log details to console
          console.warn('Validation issues:', errorMessages);
        }
      }

      showStatus('Exporting .urp file...', 'info');
      const xmlString = parser.getXML();
      const fileName = currentFileName || 'program.urp';
      URPExporter.exportToURPFile(xmlString, fileName);
      showStatus('File exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showStatus(`Export failed: ${error.message}`, 'error');
    }
  }, [parser, currentFileData, currentFileName, showStatus, validateBeforeExport]);

  const exportInstallation = useCallback(async () => {
    if (!currentInstallationData) {
      showStatus('No installation file loaded to export', 'error');
      return;
    }

    try {
      showStatus('Exporting .installation file...', 'info');
      const fileName = currentInstallationFileName || 'default.installation';
      InstallationExporter.exportToInstallationFile(currentInstallationData, fileName);
      showStatus('Installation file exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showStatus(`Export failed: ${error.message}`, 'error');
    }
  }, [currentInstallationData, currentInstallationFileName, showStatus]);

  return { exportURP, exportInstallation, validateBeforeExport };
}


